/**
 * FieldBuilderRow — one row of the backlog schema builder's `.ledger`-style
 * list, sibling to ConfigListCard's row idiom (same hover/focus-ring/
 * click-to-open-edit shell, reorder arrows, row-in stagger). Renders one
 * column-to-be: reorder arrows, label (click opens the field editor), a
 * kind/type chip (the ONE signature element — "System" for system fields,
 * the plain-words data type for custom fields), a "Required" marker, and a
 * trailing action cell (archive/restore/remove GhostIconButton, or a lock
 * glyph for the mandatory trio — never a disabled-looking dead control).
 * Shared by both the DRAFT (project-creation wizard) and LIVE (Table
 * settings) builder modes via the mode-agnostic BuilderField shape.
 */
/* eslint-disable react-refresh/only-export-components --
   this module exports the BACKLOG_FIELD_TYPE_LABELS lookup alongside the
   component so FieldEditorModal's type picker (Task 4) shares one source of
   truth; losing Fast Refresh here is accepted (ProjectForm precedent). */
import { GhostIconButton, RestoreIcon } from '@/components/GhostIconButton';
import type { BacklogFieldKind, BacklogFieldOption, BacklogFieldType, BacklogSystemField } from '@/types/backlogField';

/**
 * Mode-agnostic row shape the builder operates on. LIVE mode maps a real
 * BacklogField onto this (id = the backend id); DRAFT mode uses a client-
 * generated id since nothing is persisted yet. `locked` marks the mandatory
 * trio (RECORD_ID/DATE_FROM/DATE_TO) — always reorderable, never archivable/
 * deletable in either mode. `archived` is always false in DRAFT mode
 * (drafts have no archive concept — a removed field is just gone).
 */
export interface BuilderField {
  id: string;
  kind: BacklogFieldKind;
  systemRef: BacklogSystemField | null;
  label: string;
  dataType: BacklogFieldType | null;
  options: BacklogFieldOption[] | null;
  required: boolean;
  archived: boolean;
  locked: boolean;
}

/** Plain-words data-type labels for the kind/type chip and the
 *  FieldEditorModal type picker — one source of truth for both. */
export const BACKLOG_FIELD_TYPE_LABELS: Record<BacklogFieldType, string> = {
  TEXT: 'Text',
  LONG_TEXT: 'Long text',
  NUMBER: 'Number',
  DATE: 'Date',
  SELECT: 'Select',
  CHECKBOX: 'Checkbox',
  MEMBER: 'Member',
};

function ArrowUpIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </svg>
  );
}
function ArrowDownIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </svg>
  );
}
function LockIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}
function ArchiveIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect x="1" y="3" width="22" height="5" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  );
}
function RemoveIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function KindChip({ field }: { field: BuilderField }) {
  const text = field.kind === 'SYSTEM' ? 'System' : BACKLOG_FIELD_TYPE_LABELS[field.dataType ?? 'TEXT'];
  return (
    <span
      style={{
        display: 'inline-flex', alignItems: 'center',
        borderRadius: 'var(--radius-pill)', background: 'var(--surface-inset)',
        color: 'var(--text-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xs)',
        fontWeight: 500, padding: '3px 10px', whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );
}

export interface FieldBuilderRowTrailingAction {
  tooltip: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
  icon: 'archive' | 'restore' | 'remove';
}

export interface FieldBuilderRowProps {
  field: BuilderField;
  index: number;
  count: number;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onOpen: () => void;
  /** Omitted for a locked field (the trio renders a lock glyph instead). */
  trailingAction?: FieldBuilderRowTrailingAction;
  /** Disables both reorder arrows (e.g. a reorder mutation in flight). */
  reorderDisabled?: boolean;
  /** True to omit the reorder cell entirely. Used for rows rendered inside
   *  the Archived-columns disclosure (not part of the orderable active
   *  set) and, in DRAFT mode only, for the locked trio — the draft submit
   *  path always strips locked fields and the backend re-injects them in
   *  fixed positions, so a draft-mode trio reorder would be a silent
   *  no-op (see Task 6). LIVE mode never hides the trio's reorder cell —
   *  the backend honors trio reordering there. */
  hideReorder?: boolean;
}

// Row hover/focus idiom — copied verbatim from ConfigListCard (which itself
// copied it from CrBacklogTable): hover mixes toward --text so it steps
// lighter on dark / darker on light; focus ring is the lime-tinted brand mix.
const ROW_HOVER_BG = 'color-mix(in srgb, var(--text) 5%, transparent)';
const ROW_FOCUS_OUTLINE = '2px solid color-mix(in srgb, var(--brand) 55%, transparent)';

export function FieldBuilderRow({
  field, index, count, onMoveUp, onMoveDown, onOpen, trailingAction, reorderDisabled, hideReorder,
}: FieldBuilderRowProps) {
  return (
    <tr
      className="row-in"
      style={{ ['--i' as unknown as string]: Math.min(index, 14), opacity: field.archived ? 0.6 : 1, cursor: 'pointer' }}
      onClick={onOpen}
      tabIndex={0}
      aria-label={`Open ${field.label}`}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
          e.preventDefault();
          onOpen();
        }
      }}
      onFocus={(e) => { if (e.target === e.currentTarget) { e.currentTarget.style.outline = ROW_FOCUS_OUTLINE; e.currentTarget.style.outlineOffset = '-2px'; } }}
      onBlur={(e) => { if (e.target === e.currentTarget) { e.currentTarget.style.outline = ''; e.currentTarget.style.outlineOffset = ''; } }}
      onMouseEnter={(e) => { e.currentTarget.style.background = ROW_HOVER_BG; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
    >
      <td style={{ width: 64 }} onClick={(e) => e.stopPropagation()}>
        {!hideReorder && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <GhostIconButton tooltip="Move up" label={`Move ${field.label} up`} onClick={onMoveUp} disabled={reorderDisabled || index === 0}>
              <ArrowUpIcon />
            </GhostIconButton>
            <GhostIconButton tooltip="Move down" label={`Move ${field.label} down`} onClick={onMoveDown} disabled={reorderDisabled || index === count - 1}>
              <ArrowDownIcon />
            </GhostIconButton>
          </div>
        )}
      </td>
      <td style={{ fontWeight: 600, color: 'var(--text)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)' }}>
        {field.label}
      </td>
      <td style={{ width: 130 }}>
        <KindChip field={field} />
      </td>
      <td style={{ width: 90 }}>
        {field.required && (
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xs)', color: 'var(--sem-amber-text)', fontWeight: 600 }}>
            Required
          </span>
        )}
      </td>
      <td style={{ width: 56 }} onClick={(e) => e.stopPropagation()}>
        {field.locked ? (
          <span
            role="img"
            aria-label="Required column — can't be removed."
            title="Required column — can't be removed."
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, color: 'var(--text-subtle)' }}
          >
            <LockIcon />
          </span>
        ) : trailingAction ? (
          <GhostIconButton
            tooltip={trailingAction.tooltip}
            label={trailingAction.label}
            onClick={trailingAction.onClick}
            danger={trailingAction.danger}
          >
            {trailingAction.icon === 'archive' ? <ArchiveIcon /> : trailingAction.icon === 'restore' ? <RestoreIcon /> : <RemoveIcon />}
          </GhostIconButton>
        ) : null}
      </td>
    </tr>
  );
}
